import { CSSProperties, useEffect, useRef, useState } from "react"
import { app, translate } from "../app"
import { translations } from "../translations"
import { Link, useNavigate } from "react-router-dom"
import { languages } from "../locale"
import { groupLinksByUrlHierarchy, formatMenuTitle, menuContainsExternalLinks, servicesMenuTarget } from "../helpers"
import { ResourcesDropdown } from './resources-dropdown'
import { LanguageDropdown } from "./languagedropdown"
import MobileNavigationDrawer from './mobilenavigationdrawer'
import Container from "./menucontainer"
import { DownOutlined, MenuOutlined } from "./icons"

import * as DropdownMenu from "@radix-ui/react-dropdown-menu"

export interface MenuResources {
  title?: LocalizableText
  link?: string
  children?: MenuResources[]
  megamenu?: boolean
}


function CountrySelectorDropdown({ countrySelector }: { countrySelector: MenuResources }) {
  const [isOpen, setIsOpen] = useState(false)
  const uppercaseTitles = menuContainsExternalLinks(countrySelector?.children)

  const cs: MenuResources = countrySelector || { children: [] }
  const children = cs.children || []
  return <DropdownMenu.Root onOpenChange={() => setIsOpen(!isOpen)}>
    <DropdownMenu.Trigger asChild>
      <button type="button" className="pl-2 nm-trigger">{formatMenuTitle(translate(countrySelector?.title || {}) || "Country Selector", uppercaseTitles)}</button>
    </DropdownMenu.Trigger>
    <DownOutlined aria-hidden="true" className={`ml-2 dropdown-icon mr-2 mb-0.5 ${isOpen ? 'open' : ''}`} />
    <DropdownMenu.Portal>
      <DropdownMenu.Content sideOffset={8} className="text-black rounded-md bg-white p-1 will-change-[opacity,transform] shadow-lg w-full">
        {children.map((item, index) => {
          return <DropdownMenu.Item key={index} className="my-2 h-4 px-4 select-none items-center text-sm outline-none focus:outline-none focus-visible:outline-none">
            <a href={item.link} target="_blank" className='no-underline text-black break-words whitespace-normal outline-none focus:outline-none focus-visible:outline-none' rel="noopener noreferrer">{formatMenuTitle(translate(item.title || {}), uppercaseTitles)}</a>
          </DropdownMenu.Item>
        })}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  </DropdownMenu.Root>
}

export function Header() {
  const styles: CSSProperties = {}
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const navigate = useNavigate()
  const locale = languages[app.locale]?.zendesk as string ?? app.locale

  const drawerButtonRef = useRef(null)
  const header = app.page.header

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  if (header.color) styles.color = header.color
  if (header.bgcolor) styles.backgroundColor = header.bgcolor

  const categories = Object.values(app.data.zendesk.categories)
  const aboutMenu = header.menu.find((item) => item.type === "about")
  const resourcesMenus = header.menu.filter((item) => item.type === "menu") as Menu[]

  const extractCategoryID = (link?: string) => {
    const match = link?.match(/\/categories\/(\d+)/)
    return match ? match[1] : null
  }

  const createMenuResources = (menu: Menu): MenuResources => {
    if (!menu.megamenu) return { title: menu.title!, link: '', children: menu.content as any, megamenu: menu.megamenu! }

    const content = menu.content || []
    const categoryItems: Menu[] = []
    const otherItems: { title?: LocalizableText; link?: string }[] = []

    content.forEach((item) => {
      const categoryID = extractCategoryID(item.link)
      if (categoryID) {
        categoryItems.push(item)
        return
      }
      if (item.link) {
        otherItems.push({
          title: item.title,
          link: item.link,
        })
      }
    })

    const groupedByCategoryID = categoryItems.reduce((acc, item) => {
      const categoryID = extractCategoryID(item.link)
      if (categoryID) {
        if (!acc[categoryID]) {
          acc[categoryID] = []
        }
        acc[categoryID].push(item)
      }
      return acc
    }, {} as Record<string, Menu[]>)

    const categoryResult: MenuResources[] = Object.keys(groupedByCategoryID).flatMap((categoryID) => {
      const items = groupedByCategoryID[categoryID]
      const parentItem = items.find(item => item.link === `/categories/${categoryID}`)
      if (!parentItem) return []
      return [{
        title: parentItem.title,
        link: parentItem.link,
        children: items
          .filter(item => item.link !== `/categories/${categoryID}`)
          .map(item => ({
            title: item.title,
            link: item.link,
          })),
      }]
    })

    const externalResult = groupLinksByUrlHierarchy(otherItems) as MenuResources[]

    return {
      title: menu.title!,
      link: '',
      children: [...categoryResult, ...externalResult],
      megamenu: menu.megamenu!,
    }
  }

  const menuResourcesArray = resourcesMenus.map(createMenuResources)



  if (aboutMenu) {
    const aboutContent = aboutMenu.content || (aboutMenu.content = [])
    categories.forEach((category) => {
      if (
        !aboutContent.some(
          (item) => item.link === `/${locale.toLowerCase()}/categories/${category.id}`
        )
      ) {
        aboutContent.push({
          title: category.name,
          link: `/${locale.toLowerCase()}/categories/${category.id}`,
        })
      }
    })
  }

  const renderMenuItems = (menuItems: Menu[]) => {
    return menuItems
      .filter((item) => item.type !== 'info' && item.type !== 'menu' && item.type !== 'bot')
      .map((item) => {
        const title = item.title ? translate(item.title).toUpperCase() : ""
        let content

        if (item.type === 'about') {
          content = <a href={`/${locale.toLowerCase()}#about-section`} className="nm-trigger" onClick={() => { setIsDrawerOpen(false) }}>{title}</a>
        } else if (item.type === "services") {
          content = <a
            href={servicesMenuTarget(locale)} onClick={(e) => {
              e.preventDefault()
              navigate(servicesMenuTarget(locale))
              setIsDrawerOpen(false)
            }}
            className="nm-trigger bg-transparent border-none p-0" style={{ appearance: 'none' }}>
            {title}
          </a>

        } else {
          content = <Link to={item.link || "#"} className="nm-trigger"><h2 className="m-0">{title.toUpperCase()}</h2></Link>
        }

        return <li key={title} className="nav_item_content">{content}</li>
      })
  }

  const isRTL = languages[app.locale]?.rtl

  const renderAllMenuItems = () => {
    const menuIndexMap = new Map()
    resourcesMenus.forEach((menu, index) => {
      menuIndexMap.set(menu, index)
    })

    return header.menu
      .filter((item) => item.type !== 'info' && item.type !== 'bot')
      .map((item, originalIndex) => {
        if (item.type === 'menu') {
          const menuIndex = menuIndexMap.get(item)
          const menuResources = menuResourcesArray[menuIndex]
          if (!menuResources) return null
          
          if (!menuResources.megamenu) {
            return <li key={`menu-${originalIndex}`}><CountrySelectorDropdown countrySelector={menuResources} /></li>
          }
          return <li key={`menu-${originalIndex}`} className="list-none text-base font-normal leading-snug"><ResourcesDropdown menuResources={menuResources} /></li>
        }

        const title = item.title ? translate(item.title) : ""
        let content

        if (item.type === 'about') {
          content = <a href={`/${locale.toLowerCase()}#about-section`} className="nm-trigger" onClick={() => { setIsDrawerOpen(false) }}>{title}</a>
        } else if (item.type === "services") {
          content = <a
            href={servicesMenuTarget(locale)} onClick={(e) => {
              e.preventDefault()
              navigate(servicesMenuTarget(locale))
              setIsDrawerOpen(false)
            }}
            className="nm-trigger bg-transparent border-none p-0" style={{ appearance: 'none' }}>
            {title}
          </a>
        } else {
          content = <Link to={item.link || "#"} className="nm-trigger">{title.toUpperCase()}</Link>
        }

        return <li key={`item-${originalIndex}-${title}`} className="">{content}</li>
      })
  }

  return <div className="flex items-center tracking-wide justify-center" style={styles}>
    <nav className={`w-full px-4 sm:px-6 py-4 md:px-8 mx-auto flex items-center header-nav ${isRTL ? "rtl" : ""} ${app.country !== 42 && app.country !== 43 ? "max-w-7xl" : ""}`} style={styles}>
      <div className="logo">
      {app.logo && (app.country !== 42 && app.country !== 43) && <Link to={`/${locale.toLowerCase()}`}><img src={app.logo} alt="Logo" /></Link>}
      {app.logo && (app.country === 42 || app.country === 43) && <Link to={`https://www.switchboardta.org/`}><img src={app.logo} alt="Logo" /></Link>}
      </div>
      <Container>
        <div className="toolbar">
          <button ref={drawerButtonRef} className={`${isRTL ? 'mr-auto' : 'ml-auto'} menu_icon lg:hidden`} aria-haspopup="dialog" aria-expanded={isDrawerOpen} aria-controls="mobile-navigation" onClick={() => setIsDrawerOpen(true)} aria-label={translate(translations.navigationMenu)}>
            {/* Mobile Hamburger menu */}
            <MenuOutlined className="text-2xl" aria-hidden="true" />
          </button>
          {/* Desktop menu links */}
          <div className="hidden lg:flex items-center justify-end w-full header-links">
            <ul className='navigation-menu-list flex list-none text-base font-normal leading-snug !p-0 !m-0 ml-auto'>
              {renderAllMenuItems()}
            </ul>
            {header.menu.find(x => x.type === 'bot') && <ul className="flex items-center list-none p-0 navigation-menu-list">
              <li>
                <Link to="https://signpost-test-cunroqkoq-signpost.vercel.app/signpostbot" >Bot</Link>
              </li>
            </ul>}
          </div>
          {/* LanguageDropdwn */}
          <div className={`language-dropdown-wrapper ${isRTL ? "rtl" : ""} ${isMobile ? "mobile-style" : "desktop-style"}`} >
            <LanguageDropdown isMobile={isMobile} />
          </div>
          {/* Mobile navigation drawer */}
          <div id="mobile-navigation" data-inert={!isDrawerOpen ? "" : undefined} className="lg:hidden absolute">
            <MobileNavigationDrawer menuDataArray={menuResourcesArray} {...{ isDrawerOpen, setIsDrawerOpen, drawerButtonRef, renderMenuItems, header }} />
          </div>
        </div>
      </Container>
    </nav>
  </div>

}
